import React, { useRef } from "react";
import { Box, Card, CardContent, IconButton, Stack, Typography } from "@mui/material";
import { Close, Upload, InsertDriveFile } from "@mui/icons-material";
import { PortalTooltip } from "../../../../../helpers";
import type { Job } from "../../../../../../models";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useFieldNotesColumnViewModel } from "./FieldNotes.column.vm";
import { useValidationErrors } from "../../shared/hooks/ValidationErrorsContext";

export interface FieldNotesColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly FieldNotes column component.
 * Provides file upload and management for field notes attachments.
 */
export const FieldNotesColumnMobile: React.FC<FieldNotesColumnMobileProps> = ({
	job,
	columnContext,
	disabled = false,
	label,
}) => {
	const vm = useFieldNotesColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validationKey = "fieldNotes";
	const errorKey = getValidationErrorKey(validationKey, job.id);
	const hasError = !!validationErrors[errorKey];
	const files = vm.getCurrentFiles(job);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (!selectedFiles || selectedFiles.length === 0) return;

		vm.handleAddFiles(job.id, selectedFiles);

		// Clear the input so the same file can be selected again
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleRemoveFile = (index: number) => {
		if (disabled) return;
		vm.handleRemoveFile(job.id, index);
	};

	return (
		<Card variant="outlined">
			<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
				{/* Single-line layout: label left, icons right */}
				<Box display="flex" alignItems="center">
					<Typography variant="subtitle2" color={hasError ? "error" : "text.secondary"} noWrap>
						{label || columnDisplayNames.fieldNotes}
						{files.length > 0 && ` (${files.length})`}
					</Typography>

					<Box display="flex" alignItems="center" gap={0.5} ml="auto">
						<PortalTooltip title="Upload files">
							<span>
								<IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
									<Upload fontSize="small" />
								</IconButton>
							</span>
						</PortalTooltip>
					</Box>
				</Box>

				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					multiple
					style={{ display: "none" }}
					onChange={handleFileSelect}
					disabled={disabled}
				/>

				{/* File list */}
				{files.length > 0 && (
					<Stack spacing={0.5} sx={{ mt: 1 }}>
						{files.map((file, index) => (
							<Box
								key={index}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1,
									py: 0.5,
									bgcolor: "action.hover",
									borderRadius: 1,
								}}
							>
								<InsertDriveFile sx={{ fontSize: 16 }} color="action" />
								<Typography
									variant="body2"
									sx={{
										flex: 1,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{file.name}
								</Typography>
								<PortalTooltip title="Remove">
									<span>
										<IconButton size="small" onClick={() => handleRemoveFile(index)} disabled={disabled} color="error">
											<Close sx={{ fontSize: 16 }} />
										</IconButton>
									</span>
								</PortalTooltip>
							</Box>
						))}
					</Stack>
				)}

				{/* Error message */}
				{hasError && (
					<Typography variant="caption" color="error" display="block" mt={0.5}>
						{validationErrors[errorKey]}
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};
